/* eslint-disable no-underscore-dangle */
import React from 'react';
import styles from 'Components/PdfFormTemplates/PdfFormEditor/pdfFormEditor.module.css';
import { EditorField } from '../types';
import { DraggableField } from './DraggableField';

interface Marquee {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface SnapGuides {
  x?: number;
  y?: number;
}

interface ActiveDrag {
  clientId: string;
  transform: { x: number; y: number };
}

interface Props {
  fields: EditorField[];
  currentPage: number;
  selectedFieldIds: Set<string>;
  scale: number;
  onSelect: (clientId: string) => void;
  onToggleSelect: (clientId: string) => void;
  onResize: (clientId: string, width: number, height: number, x?: number, y?: number) => void;
  onResizeBegin?: () => void;
  onResizeEnd?: () => void;
  snapDimensions: (field: EditorField, width: number, height: number) => { width: number; height: number };
  activeDrag?: ActiveDrag | null;
  marquee?: Marquee | null;
  snapGuides?: SnapGuides | null;
}

export function FieldOverlay({ fields, currentPage, selectedFieldIds, scale, onSelect, onToggleSelect, onResize, onResizeBegin, onResizeEnd, snapDimensions, activeDrag, marquee, snapGuides }: Props) {
  const pageFields = fields.filter((f) => f.page === currentPage);

  let marqueeEl: React.ReactNode = null;
  if (marquee) {
    const left = Math.min(marquee.startX, marquee.endX) * scale;
    const top = Math.min(marquee.startY, marquee.endY) * scale;
    const width = Math.abs(marquee.endX - marquee.startX) * scale;
    const height = Math.abs(marquee.endY - marquee.startY) * scale;
    marqueeEl = (
      <div
        className={styles.marqueeRect}
        style={{ left, top, width, height }}
      />
    );
  }

  const guideEls: React.ReactNode[] = [];
  if (snapGuides?.x !== undefined) {
    guideEls.push(
      <div
        key="guide-x"
        className={styles.snapGuideVertical}
        style={{ left: snapGuides.x * scale }}
      />
    );
  }
  if (snapGuides?.y !== undefined) {
    guideEls.push(
      <div
        key="guide-y"
        className={styles.snapGuideHorizontal}
        style={{ top: snapGuides.y * scale }}
      />
    );
  }

  // When dragging a selected field, other selected fields should follow
  const isGroupDrag = activeDrag && selectedFieldIds.has(activeDrag.clientId) && selectedFieldIds.size > 1;

  return (
    <>
      {pageFields.map((field) => {
        // Apply group transform to selected fields that aren't the one being dragged
        const groupTransform = isGroupDrag && field._clientId !== activeDrag.clientId && selectedFieldIds.has(field._clientId)
          ? activeDrag.transform
          : null;
        return (
          <DraggableField
            key={field._clientId}
            field={field}
            isSelected={selectedFieldIds.has(field._clientId)}
            scale={scale}
            onSelect={onSelect}
            onToggleSelect={onToggleSelect}
            onResize={onResize}
            onResizeBegin={onResizeBegin}
            onResizeEnd={onResizeEnd}
            snapDimensions={snapDimensions}
            groupTransform={groupTransform}
          />
        );
      })}
      {marqueeEl}
      {guideEls}
    </>
  );
}
